import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'

import Button from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { Z } from '@/lib/zIndex'
import { useDialogBackHandler } from '@/hooks/useDialogBackHandler'

/**
 * <MobileAlertDialog> — the project's mobile-first confirm dialog, intended to
 * replace `window.confirm()` (which blocks the main thread on mobile). Built on
 * `@radix-ui/react-dialog` (NOT `@radix-ui/react-alert-dialog`, which is always
 * modal and would re-trigger the iOS back-swipe bug — see shadcn-adoption-plan
 * Phase 3). We set `role="alertdialog"` on the Content and block outside-click
 * dismissal so the user must choose Cancel or Confirm (alert semantics).
 *
 * Like <MobileDialog> it is mobile full-screen / desktop centered-card and uses
 * `modal={isDesktop}` (non-modal on mobile for iOS swipe-back). Escape and the
 * hardware back / swipe-back gesture cancel (call `onClose`); there is no dirty
 * guard because this dialog IS the guard.
 *
 * On Confirm: calls `onConfirm()` then closes. For an async action you'd rather
 * keep open during the request, control `open` yourself and close in
 * `onConfirm` once settled.
 *
 * Usage:
 *   <MobileAlertDialog open={open} onClose={() => setOpen(false)}
 *     onConfirm={handleDelete} title="Delete filter?"
 *     description="This cannot be undone." confirmLabel="Delete"
 *     confirmVariant="danger" />
 */
interface MobileAlertDialogProps {
  /** Controlled open state. */
  open: boolean
  /** Called on cancel / Escape / back / scrim-attempt (any non-confirm path). */
  onClose: () => void
  /** Called when the user confirms; the dialog then closes. */
  onConfirm: () => void
  /** Accessible + visible title (required by Radix for a11y). */
  title: React.ReactNode
  /** The confirm message; rendered visibly and linked via aria-describedby. */
  description?: React.ReactNode
  /** Extra body content below the description (optional). */
  children?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Confirm button style. `'danger'` for destructive confirms. Defaults to `'primary'`. */
  confirmVariant?: 'primary' | 'danger'
  /** Extra classes on the content card. */
  className?: string
  /** Extra classes on the body region. */
  bodyClassName?: string
  /** Radix `onOpenAutoFocus` handler, forwarded to the dialog content. */
  onOpenAutoFocus?: (event: Event) => void
}

export function MobileAlertDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  className,
  bodyClassName,
  onOpenAutoFocus,
}: MobileAlertDialogProps) {
  // See <MobileDialog>: modal scroll-lock breaks iOS Safari's back-swipe, so use
  // modal mode only on desktop; on mobile a full-screen confirm needs neither
  // scroll-lock nor focus-trap and iOS swipe-back is essential.
  const [isDesktop, setIsDesktop] = React.useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(min-width: 640px)').matches,
  )
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia('(min-width: 640px)')
    const onChange = () => setIsDesktop(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  // Escape / scrim-attempt → onClose (cancel). Outside-click is prevented below
  // so the scrim is not a dismissal path — alert semantics require a choice.
  useDialogBackHandler(open, onClose)

  const handleConfirm = () => {
    onConfirm()
    onClose()
  }

  const contentA11yProps = description ? {} : { 'aria-describedby': undefined }

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      modal={isDesktop}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            `fixed inset-0 z-[${Z.modal}] bg-black/40`,
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
          )}
        />
        <DialogPrimitive.Content
          {...contentA11yProps}
          role="alertdialog"
          onOpenAutoFocus={onOpenAutoFocus}
          // Alert semantics: do not dismiss on scrim / outside click — the user
          // must press Cancel or Confirm (Escape and back still cancel).
          onInteractOutside={(event) => event.preventDefault()}
          className={cn(
            // Mobile (<640): full-screen sheet covering the app header.
            `fixed inset-0 z-[${Z.modal}] flex w-full flex-col bg-background overlay-safe`,
            // Desktop (sm+): a centered card on the scrim.
            'sm:inset-auto sm:left-1/2 sm:top-1/2 sm:max-h-[calc(100dvh-3rem)] sm:w-[calc(100%-2rem)] sm:max-w-lg sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl sm:border sm:border-border sm:shadow-xl',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
            'sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95',
            className,
          )}
        >
          {/* Header — title only (no X: alert dialogs require an explicit action). */}
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-4 py-4 sm:px-6">
            <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight text-foreground">
              {title}
            </DialogPrimitive.Title>
          </div>

          <div className={cn('flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6', bodyClassName)}>
            {description ? (
              <DialogPrimitive.Description className="text-sm text-muted-foreground">
                {description}
              </DialogPrimitive.Description>
            ) : null}
            {children}
          </div>

          {/* Footer — Cancel + Confirm. DOM order [Cancel, Confirm] → on mobile
              (flex-col-reverse) Confirm sits on top; on desktop Confirm is rightmost. */}
          <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-border bg-background px-4 py-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] sm:flex-row sm:justify-end sm:gap-3 sm:pb-4">
            <Button variant="ghost" onClick={onClose}>
              {cancelLabel}
            </Button>
            <Button variant={confirmVariant} onClick={handleConfirm}>
              {confirmLabel}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

export default MobileAlertDialog
